import {
  EventSeverity,
  NotificationRuleCategory,
  NotificationRuleNotificationType,
} from '@eppendorf/vnls-notifications-utils';
import { AxiosError } from 'axios';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { createCurrentDateString } from '$shared/utils/date.utils';

import { useFeatureIsEnabled } from '$components/feature-toogle/feature-is-enabled';
import {
  allNotificationTypes,
  defaultEscalateAfterValue,
  defaultNotificationCadence,
  defaultNotificationDelay,
} from '$features/notifications/add-or-edit-notification-rule-dialog/constants';
import {
  useAddNotificationRule,
  useUpdateNotificationRule,
} from '$features/notifications/notification-rules.api';
import {
  NotificationRuleConditionMode,
  NotificationRuleDetailData,
  NotificationRuleMode,
  NotificationRuleSubmitForm,
} from '$features/notifications/types';

type NotificationRuleConditionFields = Pick<
  NotificationRuleSubmitForm,
  'conditionMode' | 'tags' | 'categories'
>;

type NotificationRuleConditionPayload =
  | { tags: EventSeverity[]; categories?: [] }
  | { tags: []; categories: NotificationRuleCategory[] };

export const createNotificationRuleConditionPayload = (
  data: NotificationRuleConditionFields,
  isCategoryConditionsEnabled: boolean,
): NotificationRuleConditionPayload => {
  if (
    isCategoryConditionsEnabled &&
    data.conditionMode === NotificationRuleConditionMode.ADVANCED
  ) {
    const categories = new Map<
      NotificationRuleCategory['key'],
      Set<NotificationRuleCategory['subCategories'][number]>
    >();

    data.categories.forEach((category) => {
      if (!category.subCategories.length) return;

      const currentSubCategories = categories.get(category.key) ?? new Set();
      category.subCategories.forEach((subCategory) =>
        currentSubCategories.add(subCategory),
      );
      categories.set(category.key, currentSubCategories);
    });

    return {
      tags: [],
      categories: [...categories].map(([key, subCategories]) => ({
        key,
        subCategories: [...subCategories],
      })),
    };
  }

  return isCategoryConditionsEnabled
    ? { tags: data.tags, categories: [] }
    : { tags: data.tags };
};

export const useNotificationRuleDialogForm = (
  notificationRuleData: NotificationRuleDetailData | undefined,
  mode: NotificationRuleMode,
) => {
  const { t } = useTranslation();
  const editMode = mode === NotificationRuleMode.EDIT;
  const isNotificationTypesEnabled = useFeatureIsEnabled(
    'ESEVNLS_4596_NOTIFICATION_TYPES',
  );
  const isCategoryConditionsEnabled = useFeatureIsEnabled(
    'ESEVNLS_4552_DEVICE_EVENT_CATEGORIES',
  );
  const defaultEscalationLayers = notificationRuleData?.escalationLayers;
  const selectedDevices = notificationRuleData?.devices.details.map((device) => ({
    id: device.serialNumber,
    value: device.serialNumber,
    model: device.model,
  }));
  const hasAdvancedConditions = Boolean(notificationRuleData?.categories?.length);
  const getDefaultNotificationTypes = (): NotificationRuleNotificationType[] => {
    if (notificationRuleData?.notificationTypes?.length) {
      return notificationRuleData.notificationTypes;
    }

    if (editMode) {
      return allNotificationTypes;
    }

    return ['Created', 'Archived', 'Expired'];
  };

  const formMethods = useForm<NotificationRuleSubmitForm>({
    defaultValues: {
      selectedDeviceType: notificationRuleData?.selectedDeviceType || undefined,
      escalationLayers: defaultEscalationLayers || [
        { escalateAfter: defaultEscalateAfterValue, recipients: [] },
      ],
      devices: selectedDevices || [],
      notificationRuleName:
        notificationRuleData?.name ||
        `${t('notificationRules.defaultNamePrefix')} - ${createCurrentDateString()}`,
      conditionMode:
        isCategoryConditionsEnabled && (!editMode || hasAdvancedConditions)
          ? NotificationRuleConditionMode.ADVANCED
          : NotificationRuleConditionMode.BASIC,
      tags: notificationRuleData?.tags || [],
      categories: notificationRuleData?.categories || [],
      notificationCadence:
        notificationRuleData?.notificationCadence ?? defaultNotificationCadence,
      notificationSendDelaySeconds:
        notificationRuleData?.notificationSendDelaySeconds ?? defaultNotificationDelay,
      notificationTypes: getDefaultNotificationTypes(),
    },
  });

  const {
    mutateAsync,
    data: addedNotificationRuleResponse,
    isPending: isLoadingAdd,
    error: requestErrorAdd,
    isSuccess: isSuccessAdd,
    reset: resetAdd,
  } = useAddNotificationRule();

  const {
    mutateAsync: mutateEditAsync,
    isPending: isLoadingEdit,
    isSuccess: isSuccessEdit,
    error: requestErrorEdit,
    reset: resetEdit,
  } = useUpdateNotificationRule();

  // Trigger validation for any field being edited
  useEffect(() => {
    const subscription = formMethods.watch((_value, { name }) =>
      formMethods.trigger(name),
    );
    return () => subscription.unsubscribe();
  }, [formMethods.watch]);

  // Watch for Backend API errors
  useEffect(() => {
    if (requestErrorAdd && requestErrorAdd.response?.status === 409) {
      formMethods.setError(
        'notificationRuleName',
        { message: t('notificationRules.error.nameAlreadyExists') },
        { shouldFocus: true },
      );
    }
  }, [requestErrorAdd]);
  useEffect(() => {
    const isAxiosError = (error: Error): error is AxiosError =>
      (error as AxiosError).isAxiosError !== undefined;

    if (
      requestErrorEdit &&
      isAxiosError(requestErrorEdit) &&
      requestErrorEdit.response?.status === 409
    ) {
      formMethods.setError(
        'notificationRuleName',
        { message: t('notificationRules.error.nameAlreadyExists') },
        { shouldFocus: true },
      );
    }
  }, [requestErrorEdit]);

  const handleFormSubmit = async (data: NotificationRuleSubmitForm) => {
    const sanitizedEscalationLayers = data.escalationLayers.map(
      // eslint-disable-next-line @typescript-eslint/no-shadow -- it's safe to destructure
      ({ mode, ...rest }) => rest,
    );
    const notificationTypes: NotificationRuleNotificationType[] = [
      ...(data.notificationTypes || []).filter((type) => type !== 'Escalated'),
      'Escalated',
    ];
    const notificationTypesPayload = isNotificationTypesEnabled
      ? { notificationTypes }
      : {};
    const conditionPayload = createNotificationRuleConditionPayload(
      data,
      isCategoryConditionsEnabled,
    );

    if (editMode) {
      try {
        await mutateEditAsync({
          id: notificationRuleData?.id || '',
          name: data.notificationRuleName,
          selectedDeviceType: data.selectedDeviceType,
          escalationLayers: sanitizedEscalationLayers,
          subjects: data.devices.map(({ id }) => ({ type: 'device', id })),
          ...conditionPayload,
          notificationCadence: data.notificationCadence,
          notificationSendDelaySeconds: data.notificationSendDelaySeconds,
          ...notificationTypesPayload,
        });
      } catch (_) {
        // already handled by the error effect
      }
    }
    if (mode === NotificationRuleMode.CREATE) {
      await mutateAsync({
        name: data.notificationRuleName,
        selectedDeviceType: data.selectedDeviceType,
        escalationLayers: sanitizedEscalationLayers,
        subjects: data.devices.map(({ id }) => ({ type: 'device', id })),
        ...conditionPayload,
        notificationCadence: data.notificationCadence,
        notificationSendDelaySeconds: data.notificationSendDelaySeconds,
        ...notificationTypesPayload,
      });
    }
  };

  return {
    handleFormSubmit,
    isLoading: editMode ? isLoadingEdit : isLoadingAdd,
    requestError: (editMode ? requestErrorEdit : requestErrorAdd) as AxiosError,
    isSuccess: editMode ? isSuccessEdit : isSuccessAdd,
    addedNotificationRule: addedNotificationRuleResponse?.data,
    formMethods,
    reset: editMode ? resetEdit : resetAdd,
  };
};
